<script>
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { useAI } from 'dashboard/composables/useAI';
import { convertHtmlToMarkdown } from 'dashboard/helper/editorHelper';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import AILoader from './AILoader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    AILoader,
    NextButton,
  },
  mixins: [keyboardEventListenerMixins],
  props: {
    aiOption: {
      type: String,
      required: true,
    },
    // Callers without a conversation (the compose modal) pass their editor content directly;
    // otherwise the selected conversation's saved draft is used.
    content: {
      type: String,
      default: null,
    },
    conversationId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ['close', 'applyText'],
  setup() {
    const { formatMessage } = useMessageFormatter();
    const {
      draftMessage,
      processEvent,
      recordAnalytics,
      translationTargetLanguageLabel,
    } = useAI();
    return {
      draftMessage,
      processEvent,
      recordAnalytics,
      formatMessage,
      translationTargetLanguageLabel,
    };
  },
  data() {
    return {
      generatedContent: '',
      isGenerating: true,
    };
  },
  computed: {
    headerTitle() {
      const translationKey = this.aiOption?.toUpperCase();
      if (!translationKey) return '';
      let option = this.$t(
        `INTEGRATION_SETTINGS.OPEN_AI.OPTIONS.${translationKey}`
      );
      // Surface the target language name for the translate action so it's clear
      // which language the draft is being translated into.
      if (
        this.aiOption === 'translate' &&
        this.translationTargetLanguageLabel
      ) {
        option = `${option} (${this.translationTargetLanguageLabel})`;
      }
      return this.$t('INTEGRATION_SETTINGS.OPEN_AI.WITH_AI', { option });
    },
    // The text this dialog acts on: the caller's own content when it has no conversation
    // (the compose modal), otherwise the selected conversation's saved draft.
    effectiveContent() {
      return this.content ?? this.draftMessage;
    },
    draftMessageMarkdown() {
      let content = this.effectiveContent;
      if (!content) return '';

      if (content.startsWith('<')) {
        content = convertHtmlToMarkdown(content);
      }

      return content;
    },
  },
  mounted() {
    this.generateAIContent(this.aiOption);
  },

  methods: {
    getKeyboardEvents() {
      return {
        Escape: {
          action: e => {
            e.preventDefault();
            this.onClose();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            e.preventDefault();
            this.applyText();
          },
          allowOnFocusedInput: true,
        },
      };
    },
    onClose() {
      this.$emit('close');
      emitter.emit(BUS_EVENTS.FOCUS_MESSAGE_EDITOR);
    },

    async generateAIContent(type = 'rephrase') {
      this.isGenerating = true;
      this.generatedContent = await this.processEvent(type, {
        content: this.content ?? undefined,
        conversationId: this.conversationId ?? undefined,
      });
      this.isGenerating = false;
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.focus();
        }
      });
    },
    applyText() {
      this.recordAnalytics(this.aiOption);
      this.$emit('applyText', this.generatedContent);
      this.onClose();
    },
  },
};
</script>

<template>
  <div class="flex flex-col">
    <woot-modal-header :header-title="headerTitle" />
    <form
      ref="form"
      class="flex flex-col w-full modal-content"
      @submit.prevent="applyText"
    >
      <div v-if="effectiveContent" class="w-full">
        <h4 class="mt-1 mb-2 text-sm font-medium text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.OPEN_AI.ASSISTANCE_MODAL.DRAFT_TITLE') }}
        </h4>
        <div
          v-dompurify-html="formatMessage(draftMessageMarkdown, false)"
          class="p-3 mb-4 rounded-lg bg-n-slate-2 text-n-slate-12 border border-n-weak"
        />
        <h4 class="mt-1 mb-2 text-sm font-medium text-n-slate-11">
          {{
            $t('INTEGRATION_SETTINGS.OPEN_AI.ASSISTANCE_MODAL.GENERATED_TITLE')
          }}
        </h4>
      </div>
      <div>
        <AILoader v-if="isGenerating" />
        <div
          v-else
          v-dompurify-html="formatMessage(generatedContent, false)"
          class="p-3 mb-4 rounded-lg bg-n-solid-blue/10 text-n-slate-12 border border-n-blue-6"
        />
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="
            $t('INTEGRATION_SETTINGS.OPEN_AI.ASSISTANCE_MODAL.BUTTONS.CANCEL')
          "
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :disabled="!generatedContent"
          :label="
            $t('INTEGRATION_SETTINGS.OPEN_AI.ASSISTANCE_MODAL.BUTTONS.APPLY')
          "
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.modal-content {
  @apply pt-2 px-8 pb-8;
}

.container {
  width: 100%;
}
</style>
